import React, { useState } from 'react';
import { FloatingChat, Header, Footer, MobileHeader } from '@/components/layout';
import { HeroSectionAnuncie } from '@/components/sections';
import { useIsMobile } from '@/hooks/use-mobile';
import { useImobiliaria } from '@/hooks/useImobiliaria';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useCreateLeadProprietario } from '@/hooks/useLeads';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ChevronDown, Check } from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';

interface HeroAnuncieProps {
  nomeSite?: string;
  tituloAnuncie?: string;
  subtituloAnuncie?: string;
}

const HeroAnuncie: React.FC<HeroAnuncieProps> = ({ nomeSite, tituloAnuncie, subtituloAnuncie }) => {
  return (
    <section className="w-full bg-gray-light">
      <div className="max-w-[1166px] mx-auto px-4 lg:px-10 py-10 lg:py-14">
        <div className="bg-white rounded-[24px] lg:rounded-[32px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 lg:p-10">
          <div className="text-center">
            <h1 className="font-black text-neutral-black text-2xl lg:text-[32px] leading-tight">
              {tituloAnuncie || `Anuncie na ${nomeSite || 'De Sordi Broker'}`}
            </h1>
            <p className="mt-2 text-neutral-gray-light text-base lg:text-xl">
              {subtituloAnuncie || 'Seu imóvel em destaque para quem está buscando de verdade.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

interface FormAnuncieProps {
  imobiliariaData?: any;
  siteConfigData?: any;
}

const FormAnuncie: React.FC<FormAnuncieProps> = ({ imobiliariaData, siteConfigData }) => {
  const [form, setForm] = useState({
    finalidade: 'alugar',
    nome: '',
    email: '',
    telefone: '',
    cidade: '',
    bairro: '',
    valor: '',
    tipo: 'Studio',
  });
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  
  const createLeadProprietario = useCreateLeadProprietario();
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação client-side
    if (!form.nome.trim() || form.nome.trim().length < 3) {
      toast({
        title: "Nome inválido",
        description: "Por favor, informe seu nome completo",
        variant: "destructive",
      });
      return;
    }

    if (!form.email.trim() && !form.telefone.trim()) {
      toast({
        title: "Contato necessário",
        description: "Por favor, informe pelo menos um email ou telefone",
        variant: "destructive",
      });
      return;
    }

    if (!form.cidade.trim()) {
      toast({
        title: "Cidade obrigatória",
        description: "Por favor, informe a cidade do imóvel",
        variant: "destructive",
      });
      return;
    }

    try {
      const imobiliariaId = imobiliariaData?.id;
      
      if (!imobiliariaId) {
        throw new Error('Imobiliária não identificada');
      }

      const payload = {
        nome: form.nome.trim(),
        email: form.email.trim() || undefined,
        telefone: form.telefone.trim() || undefined,
        imobiliariaId,
        finalidade: form.finalidade as 'alugar' | 'vender',
        cidade: form.cidade.trim(),
        bairro: form.bairro.trim() || undefined,
        valor: form.valor.trim() || undefined,
        tipo: form.tipo,
        _hp_field: honeypot, // Honeypot field for bot detection
      };

      console.log('📤 Enviando lead de proprietário:', payload);
      
      await createLeadProprietario.mutateAsync(payload);
      
      setSubmitted(true);
      
      toast({
        title: "Anúncio recebido!",
        description: "Em breve entraremos em contato para mais detalhes.",
      });
      
    } catch (error) {
      console.error('❌ Erro ao enviar anúncio:', error);
      
      toast({
        title: "Erro ao enviar",
        description: error instanceof Error 
          ? error.message 
          : "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="w-full">
      <div className="max-w-[1166px] mx-auto px-4 lg:px-10 py-8">
        {/* Card único englobando as duas colunas */}
        <div className="bg-white rounded-3xl lg:rounded-[32px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] px-4 lg:px-6 py-6 lg:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
            {/* Bloco info da imobiliária */}
            <aside className="bg-gray-light rounded-2xl p-4 lg:p-6">
              <img
                src="/logo-client.png"
                alt="Logo"
                className="h-8 w-auto mb-4"
              />
              <p className="text-neutral-gray text-sm leading-relaxed">
                A nossa imobiliária conecta pessoas a imóveis com transparência e agilidade.
              </p>
              <div className="mt-4 space-y-3 text-sm">
                <div>
                  <p className="text-neutral-gray-light">E-mail</p>
                  <p className="font-medium text-neutral-dark">
                    {imobiliariaData?.email || 'desordi@reformassemgrilo.com.br'}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-gray-light">Telefone</p>
                  <p className="font-medium text-neutral-dark">
                    {imobiliariaData?.telefone || '(19) 99603-1818'}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-gray-light">Endereço</p>
                  <p className="font-medium text-neutral-dark">
                    R. Dr. Liráucio Gomes, 110 - Cambuí Campinas - SP
                  </p>
                </div>
              </div>
            </aside>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="p-0">
                {/* Honeypot field - hidden from users, catches bots */}
                <input
                  type="text"
                  name="_hp_field"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="absolute -left-[9999px] opacity-0"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                {/* Tabs finalidade - controle unificado */}
                <div className="mb-4">
                  <div className="inline-flex rounded-full overflow-hidden border-[1px] border-neutral-black/60">
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, finalidade: "alugar" }))
                      }
                      className={cn(
                        "h-10 px-6 text-base font-semibold focus:outline-none transition-colors",
                        form.finalidade === "alugar"
                          ? "bg-neutral-black/10 text-neutral-black"
                          : "bg-white text-neutral-black hover:bg-neutral-black/5 hover:text-neutral-black",
                      )}
                      aria-pressed={form.finalidade === "alugar"}
                    >
                      Alugar
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, finalidade: "vender" }))
                      }
                      className={cn(
                        "h-10 px-6 text-base font-semibold focus:outline-none border-l-[1px] border-neutral-black/60 transition-colors",
                        form.finalidade === "vender"
                          ? "bg-neutral-black/10 text-neutral-black ring-neutral-black"
                          : "bg-white text-neutral-black border-neutral-black hover:bg-neutral-black/5 hover:text-neutral-black",
                      )}
                      aria-pressed={form.finalidade === "vender"}
                    >
                      Vender
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm text-neutral-gray-light">
                      Nome
                    </label>
                    <input
                      name="nome"
                      value={form.nome}
                      onChange={handleChange}
                      className="w-full h-11 rounded-full border border-gray-300 px-4 outline-none"
                      placeholder="Maria da Silva"
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm text-neutral-gray-light">
                      E-mail
                    </label>
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full h-11 rounded-full border border-gray-300 px-4 outline-none"
                      placeholder="email@dominio.com"
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm text-neutral-gray-light">
                      Telefone (opcional)
                    </label>
                    <input
                      name="telefone"
                      value={form.telefone}
                      onChange={handleChange}
                      className="w-full h-11 rounded-full border border-gray-300 px-4 outline-none"
                      placeholder="(11) 99999-1111"
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm text-neutral-gray-light">
                      Cidade
                    </label>
                    <input
                      name="cidade"
                      value={form.cidade}
                      onChange={handleChange}
                      className="w-full h-11 rounded-full border border-gray-300 px-4 outline-none"
                      placeholder="Campinas"
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-2">
                    <label className="text-sm text-neutral-gray-light">
                      Bairro (opcional)
                    </label>
                    <input
                      name="bairro"
                      value={form.bairro}
                      onChange={handleChange}
                      className="w-full h-11 rounded-full border border-gray-300 px-4 outline-none"
                      placeholder="Taquaral"
                    />
                  </div>
                  <div className="space-y-2 lg:col-span-1">
                    <label className="text-sm text-neutral-gray-light">
                      Valor pretendido
                    </label>
                    <div className="relative">
                      <input
                        name="valor"
                        value={form.valor}
                        onChange={handleChange}
                        className="w-full h-11 rounded-full border border-gray-300 px-4 pr-10 outline-none"
                        placeholder="A definir"
                      />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="space-y-2 lg:col-span-1">
                    <label className="text-sm text-neutral-gray-light">
                      Tipo de imóvel
                    </label>
                    <Select
                      value={form.tipo}
                      onValueChange={(v) =>
                        setForm((prev) => ({ ...prev, tipo: v }))
                      }
                    >
                      <SelectTrigger className="h-11 w-full rounded-full border border-gray-300 px-4 text-neutral-gray">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="Studio">Studio</SelectItem>
                        <SelectItem value="Apartamento">Apartamento</SelectItem>
                        <SelectItem value="Casa">Casa</SelectItem>
                        <SelectItem value="Loft">Loft</SelectItem>
                        <SelectItem value="Sobrado">Sobrado</SelectItem>
                        <SelectItem value="Cobertura">Cobertura</SelectItem>
                        <SelectItem value="Terreno">Terreno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={createLeadProprietario.isPending}
                    className="w-full h-11 rounded-full bg-neutral-black hover:bg-neutral-black/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-medium"
                  >
                    {createLeadProprietario.isPending 
                      ? 'Enviando...' 
                      : 'Quero anunciar meu imóvel'
                    }
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-4 lg:p-6 flex flex-col items-center justify-center text-center gap-4 min-h-[360px] animate-fade-slide-up">
                <div className="w-28 h-28 rounded-full border-[10px] border-brand-gold bg-gray-300 flex items-center justify-center">
                  <Check
                    className="w-12 h-12 text-brand-gold"
                    strokeWidth={4}
                  />
                </div>
                <h3 className="text-2xl lg:text-4xl font-black text-neutral-black">
                  Tudo certo com seu envio
                </h3>
                <p className="text-neutral-gray text-base lg:text-xl max-w-2xl">
                  Recebemos suas informações e em breve entraremos em contato
                  para seguir com a divulgação do seu imóvel.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-4 h-12 w-full rounded-full border-2 border-neutral-black text-neutral-black hover:bg-neutral-black hover:text-white transition-colors"
                >
                  Preencher novo formulário
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Anuncie() {
  const isMobile = useIsMobile();
  
  // Buscar dados da imobiliária
  const { data: imobiliariaData } = useImobiliaria('desordibroker');
  const { data: siteConfigData } = useSiteConfig(imobiliariaData?.id);

  return (
    <div className="min-h-screen bg-white">
      <div className={cn('w-full mx-auto', isMobile ? 'max-w-[600px]' : 'max-w-[1920px]')}>
        {isMobile ? <MobileHeader /> : (
          <Header 
            onNavigateToProperties={() => {}}
            onNavigateToAbout={() => {}}
            onAnnounceClick={() => {}}
          />
        )}

        <HeroAnuncie 
          nomeSite={imobiliariaData?.nome}
          tituloAnuncie={`Anuncie na ${imobiliariaData?.nome || 'De Sordi Broker'}`}
          subtituloAnuncie="Seu imóvel em destaque para quem está buscando de verdade."
        />
        <FormAnuncie 
          imobiliariaData={imobiliariaData}
          siteConfigData={siteConfigData}
        />

        <Footer />
      </div>
      
      <FloatingChat />
    </div>
  );
}


